// Brickminded — Soft header runtime.
//
// Custom element <bm-header> handles:
//  · Live cart count updates via the theme's pub/sub bus
//    (PUB_SUB_EVENTS.cartUpdate from assets/constants.js)
//  · Mobile drawer close (button + backdrop click)
//  · Mobile search: the lupe in the icon row opens the header search as a
//    pill below the bar (CSS keys on data-search-open) and focuses the field
//  · Escape key closes drawer / search

class BmHeader extends HTMLElement {
  constructor() {
    super();
    this.cartCountEl = this.querySelector('[data-bm-cart-count]');
    this.drawer = this.querySelector('[data-bm-drawer]');
    this.closeBtns = this.querySelectorAll('[data-bm-drawer-close]');
    this.searchToggle = this.querySelector('[data-bm-search-toggle]');
    this.searchWrap = this.querySelector('[data-bm-searchwrap]');
    this.nav = this.querySelector('[data-bm-nav]');
    this.navlist = this.querySelector('[data-bm-navlist]');
    this.fitNav = this.fitNav.bind(this);
  }

  connectedCallback() {
    if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe === 'function') {
      this.unsubCart = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
    }

    const cartDrawer = document.querySelector('cart-drawer');
    const cartLink = this.querySelector('[data-bm-cart-link]');
    if (cartDrawer && cartLink) {
      cartLink.addEventListener('click', (e) => {
        e.preventDefault();
        cartDrawer.open(cartLink);
      });
    }

    if (this.drawer) {
      this.closeBtns.forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.drawer.removeAttribute('open');
        })
      );

      // Lock body scroll when drawer is open
      this.observer = new MutationObserver(() => {
        if (this.drawer.hasAttribute('open')) {
          document.body.style.overflow = 'hidden';
          this.closeSearch();
        } else {
          document.body.style.overflow = '';
        }
      });
      this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['open'] });
    }

    if (this.searchToggle && this.searchWrap) {
      this.searchInput = this.searchWrap.querySelector('input[type="search"]');
      this.searchToggle.addEventListener('click', () => {
        if (this.isSearchOpen()) {
          this.closeSearch(true);
        } else {
          this.openSearch();
        }
      });
      // Closing from outside, two ways:
      //  · the dim layer over the page is this element's ::before, so a tap on
      //    it targets the element itself. Closed on click, not pointerdown:
      //    gone at pointerdown, the layer would let the same tap's click fall
      //    through onto whatever link sits underneath.
      //  · anything outside the header that the layer does not cover (the
      //    announcement bar above it, or the whole page when the header is not
      //    sticky). pointerdown, because iOS Safari does not send clicks on
      //    plain page content up to document.
      this.addEventListener('click', (e) => {
        if (e.target === this && this.isSearchOpen()) this.closeSearch();
      });
      this.outsideHandler = (e) => {
        if (this.isSearchOpen() && !this.contains(e.target)) this.closeSearch();
      };
      document.addEventListener('pointerdown', this.outsideHandler);
    }

    // Escape key
    this.escHandler = (e) => {
      if (e.key !== 'Escape') return;
      if (this.drawer && this.drawer.hasAttribute('open')) {
        this.drawer.removeAttribute('open');
      } else if (this.isSearchOpen()) {
        this.closeSearch(true);
      }
    };
    document.addEventListener('keydown', this.escHandler);

    // Scale the pill nav to its real available width: measure the items against
    // the pill and set --nav-scale to exactly the factor needed to fit (down to
    // a floor). Keeps the items inside the card no matter how many there are.
    if (this.nav && this.navlist) {
      if (typeof ResizeObserver !== 'undefined') {
        this.navRO = new ResizeObserver(this.fitNav);
        this.navRO.observe(this.nav);
      } else {
        window.addEventListener('resize', this.fitNav);
      }
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this.fitNav);
      }
      this.fitNav();
    }
  }

  isSearchOpen() {
    return this.hasAttribute('data-search-open');
  }

  openSearch() {
    if (this.drawer) this.drawer.removeAttribute('open');
    this.setAttribute('data-search-open', '');
    this.searchToggle.setAttribute('aria-expanded', 'true');
    // Synchronously, inside the tap: only then does iOS open the keyboard.
    // The pill is display:none until the attribute above is set; focus()
    // forces the style update, so the field is focusable by now.
    if (this.searchInput) this.searchInput.focus();
  }

  closeSearch(returnFocus = false) {
    if (!this.isSearchOpen()) return;
    this.removeAttribute('data-search-open');
    this.searchToggle.setAttribute('aria-expanded', 'false');
    // The results panel belongs to <predictive-search>; close it with the pill
    // so it is not standing open the next time the lupe is tapped.
    if (typeof this.searchWrap.close === 'function') this.searchWrap.close();
    if (returnFocus) {
      this.searchToggle.focus();
    } else if (this.searchInput && document.activeElement === this.searchInput) {
      this.searchInput.blur();
    }
  }

  fitNav() {
    if (!this.nav || !this.navlist) return;
    // Skip while the pill nav is hidden (mobile burger mode).
    if (getComputedStyle(this.nav).display === 'none') return;

    const cs = getComputedStyle(this.navlist);
    const avail = this.navlist.clientWidth - parseFloat(cs.paddingLeft || 0) - parseFloat(cs.paddingRight || 0);
    const cur = parseFloat(this.nav.style.getPropertyValue('--nav-scale')) || 1;

    const items = this.navlist.children;
    let need = 0;
    for (let i = 0; i < items.length; i++) need += items[i].getBoundingClientRect().width;
    const gap = parseFloat(cs.columnGap || cs.gap || 0) || 0;
    need += gap * Math.max(0, items.length - 1);
    if (need <= 0 || avail <= 0) return;

    // Item widths scale ~linearly with --nav-scale, so full-size need = need / cur.
    const full = need / cur;
    const FLOOR = 0.72;
    const target = full > avail ? Math.max(FLOOR, avail / full) : 1;

    if (Math.abs(target - cur) > 0.005) {
      this.nav.style.setProperty('--nav-scale', target.toFixed(3));
    }
  }

  disconnectedCallback() {
    if (this.unsubCart) this.unsubCart();
    if (this.observer) this.observer.disconnect();
    if (this.navRO) this.navRO.disconnect();
    window.removeEventListener('resize', this.fitNav);
    if (this.escHandler) document.removeEventListener('keydown', this.escHandler);
    if (this.outsideHandler) document.removeEventListener('pointerdown', this.outsideHandler);
    document.body.style.overflow = '';
  }

  onCartUpdate(payload) {
    if (!this.cartCountEl) return;
    const cart = payload && payload.cartData;
    if (cart && typeof cart.item_count === 'number') {
      this.cartCountEl.textContent = cart.item_count;
    } else {
      // Fallback: fetch /cart.js
      fetch(window.routes && window.routes.cart_url ? window.routes.cart_url + '.js' : '/cart.js')
        .then((r) => r.json())
        .then((c) => {
          this.cartCountEl.textContent = c.item_count;
        })
        .catch(() => {});
    }
  }
}

if (!customElements.get('bm-header')) {
  customElements.define('bm-header', BmHeader);
}
